import React, { useState, useEffect, useCallback } from 'react';
import { Card, Form, Button, Alert, Container, Row, Col } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { getApiUrl } from '../config/api';

interface ResetPasswordPageProps {
  onPasswordSet?: () => void;
}

const ResetPasswordPage: React.FC<ResetPasswordPageProps> = ({ onPasswordSet }) => {
  const { t } = useTranslation();
  const [token, setToken] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [tokenValid, setTokenValid] = useState<boolean | null>(null); // null = checking, true = valid, false = invalid
  const [validationError, setValidationError] = useState<string>('');
  const { resetPassword, error, isLoading, clearError, passwordResetSuccess, clearPasswordResetSuccess } = useAuth();

  const validateToken = useCallback(async (tokenToValidate: string) => {
    // Don't validate if password reset was already successful
    if (passwordResetSuccess) {
      return;
    }

    try {
      const response = await fetch(getApiUrl('/api/web/auth/validate-reset-token'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ token: tokenToValidate }),
      });

      if (response.ok) {
        setTokenValid(true);
      } else {
        setTokenValid(false);
      }
    } catch (err) {
      console.error('Token validation failed:', err);
      setTokenValid(false);
    }
  }, [passwordResetSuccess]);

  useEffect(() => {
    // Extract token from URL parameters and validate it
    const urlParams = new URLSearchParams(window.location.search);
    const tokenParam = urlParams.get('token');
    if (tokenParam) {
      setToken(tokenParam);
      validateToken(tokenParam);
    } else {
      setTokenValid(false);
    }
  }, [validateToken]);

  // Prevent token validation after successful password reset
  useEffect(() => {
    if (passwordResetSuccess) {
      // Override token validation state to prevent error messages
      setTokenValid(true);
    }
  }, [passwordResetSuccess]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    clearError();
    setValidationError('');

    if (!token || !password || !confirmPassword) {
      return;
    }

    if (password !== confirmPassword) {
      setValidationError(t('auth.resetPassword.errors.passwordMismatch'));
      return;
    }

    if (password.length < 6) {
      setValidationError(t('auth.resetPassword.errors.passwordTooShort'));
      return;
    }

    await resetPassword(token, password);
    // Success state is now managed in AuthContext
  };

  // Show loading while validating token
  if (tokenValid === null) {
    return (
      <Container className="py-5">
        <Row className="justify-content-center">
          <Col md={6} lg={4}>
            <Card>
              <Card.Body className="text-center">
                <div className="spinner-border" role="status">
                  <span className="visually-hidden">{t('common.loading')}</span>
                </div>
                <p className="mt-2">{t('auth.resetPassword.validatingToken')}</p>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    );
  }

  // Show error if token is invalid or expired
  if (tokenValid === false) {
    return (
      <Container className="py-5">
        <Row className="justify-content-center">
          <Col md={6} lg={4}>
            <Card>
              <Card.Body className="text-center">
                <div className="text-danger mb-3">
                  <div style={{ fontSize: '4rem' }}>❌</div>
                </div>
                <h4 className="mb-3 text-danger">{t('auth.resetPassword.linkExpired')}</h4>
                <p className="mb-4">
                  {t('auth.resetPassword.linkExpiredMessage')}
                </p>
                <div className="alert alert-info">
                  <small>
                    <strong>Tip:</strong> {t('auth.resetPassword.linkExpiredTip')}
                  </small>
                </div>
                <Button
                  variant="primary"
                  onClick={() => {
                    // Clear URL parameters and redirect to login page with password reset form
                    window.history.replaceState({}, '', window.location.pathname);
                    // We need to set a flag to show password reset form on login page
                    window.location.href = '/?showPasswordReset=true';
                  }}
                >
                  {t('auth.resetPassword.requestNewLink')}
                </Button>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    );
  }

  if (passwordResetSuccess) {
    return (
      <Container className="py-5">
        <Row className="justify-content-center">
          <Col md={6} lg={4}>
            <Card>
              <Card.Body className="text-center">
                <div className="text-success mb-3">
                  <div style={{ fontSize: '4rem' }}>✅</div>
                </div>
                <h4 className="mb-3 text-success">{t('auth.resetPassword.passwordSetSuccess')}</h4>
                <p className="mb-4">
                  {t('auth.resetPassword.passwordSetMessage')}
                </p>
                <div className="alert alert-info">
                  <small>
                    <strong>Tip:</strong> {t('auth.resetPassword.passwordSetTip')}
                  </small>
                </div>
                <Button
                  variant="primary"
                  href="/"
                  as="a"
                  className="me-2"
                >
                  {t('auth.resetPassword.loginButton')}
                </Button>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    );
  }

  return (
    <Container className="py-5">
      <Row className="justify-content-center">
        <Col md={6} lg={4}>
          <Card>
            <Card.Body>
              <h4 className="text-center mb-4">{t('auth.resetPassword.setPasswordTitle')}</h4>

              {error && (
                <Alert variant="danger" dismissible onClose={clearError}>
                  {error}
                </Alert>
              )}

              {validationError && (
                <Alert variant="danger" dismissible onClose={() => setValidationError('')}>
                  {validationError}
                </Alert>
              )}

              <Form onSubmit={handleSubmit}>
                {!token && (
                  <Form.Group className="mb-3">
                    <Form.Label>{t('auth.resetPassword.resetToken')}</Form.Label>
                    <Form.Control
                      type="text"
                      value={token}
                      onChange={(e) => setToken(e.target.value)}
                      placeholder={t('auth.resetPassword.resetTokenPlaceholder')}
                      required
                      disabled={isLoading}
                    />
                    <Form.Text className="text-muted">
                      {t('auth.resetPassword.resetTokenHelp')}
                    </Form.Text>
                  </Form.Group>
                )}

                <Form.Group className="mb-3">
                  <Form.Label>{t('auth.resetPassword.newPassword')}</Form.Label>
                  <Form.Control
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder={t('auth.resetPassword.newPasswordPlaceholder')}
                    minLength={6}
                    required
                    disabled={isLoading}
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>{t('auth.resetPassword.confirmPassword')}</Form.Label>
                  <Form.Control
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder={t('auth.resetPassword.confirmPasswordPlaceholder')}
                    minLength={6}
                    required
                    disabled={isLoading}
                  />
                </Form.Group>

                <div className="d-grid gap-2">
                  <Button
                    type="submit"
                    variant="primary"
                    disabled={isLoading || !token || !password || !confirmPassword || password !== confirmPassword}
                  >
                    {isLoading ? t('auth.loading') : t('auth.resetPassword.setPasswordButton')}
                  </Button>
                </div>
              </Form>

              <div className="text-center mt-3">
                <Button
                  variant="link"
                  onClick={() => window.location.href = '/'}
                  disabled={isLoading}
                >
                  {t('auth.resetPassword.backToLogin')}
                </Button>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default ResetPasswordPage;